import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tournamentApi } from '../../services/api';

type TournamentStatus = 'upcoming' | 'ongoing' | 'completed';

interface Tournament {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: TournamentStatus;
  total_teams: number;
  total_matches: number;
  prize_pool: number;
}

interface TournamentState {
  tournaments: Tournament[];
  loading: boolean;
  error: string | null;
}

const initialState: TournamentState = {
  tournaments: [],
  loading: false,
  error: null,
};

export const fetchTournaments = createAsyncThunk(
  'tournaments/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tournamentApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tournaments');
    }
  }
);

export const createTournament = createAsyncThunk(
  'tournaments/create',
  async (tournamentData: Omit<Tournament, 'id'>, { rejectWithValue }) => {
    try {
      const response = await tournamentApi.create(tournamentData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create tournament');
    }
  }
);

export const updateTournament = createAsyncThunk(
  'tournaments/update',
  async ({ id, data }: { id: number; data: Partial<Tournament> }, { rejectWithValue }) => {
    try {
      const response = await tournamentApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tournament');
    }
  }
);

export const deleteTournament = createAsyncThunk(
  'tournaments/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await tournamentApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tournament');
    }
  }
);

const tournamentSlice = createSlice({
  name: 'tournaments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tournaments
      .addCase(fetchTournaments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournaments.fulfilled, (state, action) => {
        state.loading = false;
        state.tournaments = action.payload;
      })
      .addCase(fetchTournaments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create tournament
      .addCase(createTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTournament.fulfilled, (state, action) => {
        state.loading = false;
        state.tournaments.push(action.payload);
      })
      .addCase(createTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update tournament
      .addCase(updateTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTournament.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tournaments.findIndex((tournament) => tournament.id === action.payload.id);
        if (index !== -1) {
          state.tournaments[index] = action.payload;
        }
      })
      .addCase(updateTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete tournament
      .addCase(deleteTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTournament.fulfilled, (state, action) => {
        state.loading = false;
        state.tournaments = state.tournaments.filter((tournament) => tournament.id !== action.payload);
      })
      .addCase(deleteTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = tournamentSlice.actions;
export default tournamentSlice.reducer; 